<template>
  <a-row class="volunteering-stat">
    <a-col class="volunteering-stat__chart-box">
      <h4>Опыт волонтёрства</h4>
      <div class="chart">
        <div id="arrow" :style="{ transform: 'rotate(' + arrowRotateAngle() + 'deg)' }"></div>
        <canvas ref="barCanvas" id="bar" width="176" height="176"></canvas>
      </div>
    </a-col>
    <a-col class="volunteering-stat__achievements-box">
      <white-block class="achievements">
        <a-row class="achievements-list">
          <a-col class="achievement achievement-1" :class="{ active: amount >= 20 }">
            <div class="icon">
              <img src="./assets/achievement-1.svg" alt=""/>
            </div>
            <h3 class="title">20 дней волонтёрства</h3>
            <div class="bar"></div>
          </a-col>
          <a-col class="achievement achievement-2" :class="{ active: amount >= 40 }">
            <div class="icon">
              <img src="./assets/achievement-2.svg" alt=""/>
            </div>
            <h3 class="title">40 дней волонтёрства</h3>
            <div class="bar"></div>
          </a-col>
          <a-col class="achievement achievement-3" :class="{ active: amount >= 60 }">
            <div class="icon">
              <img src="./assets/achievement-3.svg" alt=""/>
            </div>
            <h3 class="title">60 дней волонтёрства</h3>
            <div class="bar"></div>
          </a-col>
          <a-col class="achievement achievement-4" :class="{ active: amount >= 80 }">
            <div class="icon">
              <img src="./assets/achievement-4.svg" alt=""/>
            </div>
            <h3 class="title">80 дней волонтёрства</h3>
            <div class="bar"></div>
          </a-col>
        </a-row>
      </white-block>
    </a-col>
  </a-row>
</template>

<script setup>
import {defineProps, onMounted, ref, watch} from 'vue';
import WhiteBlock from "@/components/blocks/WhiteBlock.vue";

const props = defineProps({
  amount: {
    type: Number,
    required: true
  }
});

const arrowRotateAngle = () => {
  const a0 = 58;
  const a1 = 302;
  const l = props.amount;
  if (l === 0) return a0;
  if (l >= 80) return a1;
  return Math.ceil(l * (a1 - a0) / 80 + a0);
};
const barCanvas = ref(null);
const barContent = ref(null);

onMounted(() => {
  barContent.value = barCanvas.value.getContext('2d');
});

watch(
    () => props.amount,
    () => {
      paint(props.amount);
    }
);

const paint = (a) => {
  const ctx = barContent.value;
  ctx.clearRect(0, 0, barCanvas.value.width, barCanvas.value.height);

  if (a <= 0) return;
  if (a > 80) a = 80;

  for (let i = 0; i <= a; i++) {
    ctx.beginPath();
    ctx.fillStyle = generateColor(i, a);
    ctx.arc(x(i), y(i), 9, 0, Math.PI * 2);
    ctx.fill();
  }
}

const ox = 88;
const oy = 88;
const r = 79;

const startColor = hexToRgb("#53AD7F");
const endColor = hexToRgb("#579B15");

function x(a) {
  return ox + r * Math.cos(angle(a));
}

function y(a) {
  return oy + r * Math.sin(angle(a));
}

function toRad(a) {
  return a * (Math.PI / 180);
}

function angle(a) {
  return toRad(150 + 3 * a);
}

function hexToRgb(hex) {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
}

function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

function generateColor(i, a) {
  const t = a === 1 ? 1 : i / (a - 1); // интерполяционный коэффициент
  const r = Math.round(startColor.r + (endColor.r - startColor.r) * t);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * t);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * t);
  return rgbToHex(r, g, b);
}

</script>

<style lang="scss" scoped>
@use "@/assets/styles/_breakpoints.scss" as *;
@use "@/assets/styles/_colors.scss" as *;

.volunteering-stat {
  margin-bottom: 20px;
  width: 100%;
  flex-wrap: nowrap;
  gap: 35px;

  &__content {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 20px;
  }

  &__chart-box {

  }

  &__achievements-box {
    flex: 1 1 100%;
  }

  .chart {
    position: relative;
    width: 176px;
    height: 176px;
    background: url("./assets/chart-bg.svg") no-repeat center center;
    display: flex;
    align-items: center;
    justify-content: center;

    #arrow {
      position: absolute;
      width: 115px;
      height: 115px;
      background: url("./assets/arrow.svg") no-repeat center center;
    }

    #bar {
      position: absolute;
      width: 176px;
      height: 176px;
    }
  }

  .year {
    margin: -10px 0 0 20px;
  }

  .achievements {
    width: 100%;
    height: 100%;

    &-list {
      width: 100%;
      height: 100%;
      gap: 20px;

      .achievement {
        height: 227px;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        flex: 1 1 auto;

        @include breakpoint(max, 1071.999px) {
          flex: 0 0 100%;
          height: 160px;
        }
        @include breakpoint(between, 1072px 1449.999px) {
          height: 160px;
          flex: 1 1 45%;
        }

        .icon {
          width: 65px;
          height: 65px;
          box-shadow: 0px 4px 15px 2px #00000040;
          border-radius: 100%;
          background: rgba(119, 132, 172, 0.6);
          margin-bottom: 45px;
          margin-top: auto;
          display: flex;
          justify-content: center;
        }

        .title {
          font-family: "Nunito Sans", sans-serif;
          font-weight: 700;
          font-size: 12px;
          line-height: 100%;
          color: $color-text-secondary-2;
          margin: 0 3px;
          max-width: 100px;
          height: 37px;
        }

        .bar {
          width: 100%;
          height: 4px;
          border-radius: 2px;
          background: #7784AC;
        }

        &.active {
          .title {
            color: $color-text-secondary-1;
          }
        }

        &.achievement-1.active {
          .icon {
            background: radial-gradient(60.13% 60.13% at 74.4% 26.2%, #F9B897 0%, #F79F72 27.27%, #F58146 84.32%, #F2631B 100%);
          }

          .bar {
            background: #F2631B;
          }
        }

        &.achievement-2.active {
          .icon {
            background: radial-gradient(60.13% 60.13% at 74.4% 26.2%, #B0D9C4 0%, #91CBAD 27.27%, #72BC96 84.32%, #53AD7F 100%);
          }

          .bar {
            background: #53AD7F;
          }
        }

        &.achievement-3.active {
          .icon {
            background: radial-gradient(60.13% 60.13% at 74.4% 26.2%, #72A7EE 0%, #628DC8 27.27%, #4387E2 84.32%, #2F80ED 100%);
          }

          .bar {
            background: #2F80ED;
          }
        }

        &.achievement-4.active {
          .icon {
            background: radial-gradient(60.13% 60.13% at 74.4% 26.2%, #A97CD6 0%, #9260C5 27.27%, #8349BB 84.32%, #7331B4 100%);
          }

          .bar {
            background: #7331B4;
          }
        }
      }
    }
  }
}
</style>